import { useEffect, useMemo, useState } from 'react';
import { Button, Icon, Message } from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import { ReactTable, LoadingWrapper, SelectColumnFilter, SelectFilterType } from '../../common';
import { formatDateToMmDdYyyy } from '../../../utils/formatDate';
import { deleteQuestion } from './questionFormService';
import { isICCSupport } from '../../auth';

export const QuestionsFormsContent = ({ questions, onRefresh }) => {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();
    const canManage = isICCSupport();

    useEffect(() => {
        setLoaded(false);
        setRows(Array.isArray(questions) ? questions : []);
        setLoaded(true);
    }, [questions]);

    const statusOptions = useMemo(() => ['Open', 'Closed'], []);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: 'Submitted Date',
                accessor: 'submittedDate',
                Cell: ({ row }) => formatDateToMmDdYyyy(row.original.submittedDate),
            },
            {
                Header: 'Associate',
                accessor: 'submittedByName',
            },
            {
                Header: 'Target Respondent',
                accessor: 'targetRespondentName',
                Cell: ({ row }) => row.original.targetRespondentName || '—',
            },
            {
                Header: 'Status',
                accessor: 'statusDisplay',
                Filter: row => SelectColumnFilter(row, statusOptions),
                filter: SelectFilterType,
            },
            {
                Header: 'Answered By',
                accessor: 'answeredByName',
                Cell: ({ row }) => row.original.answeredByName || '—',
            },
            {
                Header: 'View',
                accessor: 'id',
                Cell: ({ row }) => (
                    <Link to={`/forms/questions/${row.original.id}`}>Open</Link>
                ),
            },
        ];

        if (canManage) {
            baseColumns.push({
                Header: 'Delete',
                accessor: 'deleteAction',
                Cell: ({ row }) => (
                    <Button
                        size="mini"
                        basic
                        color="red"
                        loading={deletingId === row.original.id}
                        disabled={deletingId !== null}
                        onClick={async () => {
                            if (!window.confirm('Delete this question form? This cannot be undone.')) {
                                return;
                            }
                            setError('');
                            setSuccess('');
                            setDeletingId(row.original.id);
                            try {
                                await deleteQuestion(row.original.id);
                                setSuccess('Question form deleted.');
                                if (onRefresh) {
                                    await onRefresh();
                                }
                            } catch (err) {
                                setError(err.message);
                            } finally {
                                setDeletingId(null);
                            }
                        }}
                    >
                        Delete
                    </Button>
                ),
            });
        }

        return baseColumns;
    }, [canManage, statusOptions, deletingId, onRefresh]);

    return (
        <LoadingWrapper loading={!loaded} text="Loading question forms">
            <div>
                {error && <Message negative>{error}</Message>}
                {success && <Message positive>{success}</Message>}
                <ReactTable
                    className="channel-table"
                    data={rows}
                    columns={columns}
                    editable={false}
                />
                <div style={{ padding: '1rem 0', display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <Button
                        style={{ backgroundColor: 'var(--add)', color: 'white' }}
                        onClick={() => navigate('/forms/questions/new')}
                        icon
                        labelPosition="left"
                    >
                        <Icon name="plus" />
                        New Question
                    </Button>
                </div>
            </div>
        </LoadingWrapper>
    );
};
